import { useRef } from 'react';
import { useQuery } from 'react-query';
import {
  getLeaseSigningProvider,
  getBlueMoonActiveProperties,
} from '../services/leaseSigningProviderService';

type BlockedReason =
  | 'LINK_MISSING'
  | 'CONNECTION_MISSING'
  | 'CONNECTION_BROKEN'
  | null;

type LeaseProviderResolution = {
  provider: 'BLUEMOON' | 'FORTRESS' | 'UNKNOWN',
  isUsable: boolean,
  basis: string,
  blockedReason: BlockedReason,
};

// staleTime only marks the answer stale. The interval refreshes an open page after Support changes a
// property, without a focus change or remount. Both queries use it.
export const LEASE_SIGNING_PROVIDER_REFRESH_MS = 60000;

// A failed refresh keeps the last good answer for this long (five refreshes), so one network blip
// does not disable the lease actions or revert an unsaved choice. A longer outage is unknown again.
export const STALE_ANSWER_LIMIT_MS = 5 * LEASE_SIGNING_PROVIDER_REFRESH_MS;

const lastGoodAnswer = ({ data, isError, dataUpdatedAt }) =>
  isError && Date.now() - dataUpdatedAt > STALE_ANSWER_LIMIT_MS
    ? undefined
    : data;

type ProviderParams = {
  organizationId: ?string,
  propertyId: ?string,
  leaseId?: ?string,
  enabled?: boolean,
};

export const useLeaseSigningProvider = ({
  organizationId,
  propertyId,
  leaseId,
  enabled,
}: ProviderParams) => {
  const isEnabled = Boolean(enabled && organizationId && propertyId);
  const query = useQuery(
    ['lease-signing-provider', organizationId, propertyId, leaseId ?? null],
    () => getLeaseSigningProvider(organizationId, propertyId, leaseId),
    {
      enabled: isEnabled,
      staleTime: 30000,
      refetchInterval: isEnabled ? LEASE_SIGNING_PROVIDER_REFRESH_MS : false,
      retry: false,
    },
  );
  const { isLoading, isError, refetch } = query;

  if (!enabled) {
    return {
      provider: 'FORTRESS',
      isUsable: true,
      blockedReason: null,
      isLoading: false,
      isError: false,
      refetch,
    };
  }

  const resolution: ?LeaseProviderResolution = lastGoodAnswer(query);
  return {
    provider: resolution?.provider ?? 'UNKNOWN',
    isUsable: resolution?.isUsable ?? false,
    blockedReason: resolution?.blockedReason ?? null,
    isLoading,
    isError: isError && resolution === undefined,
    refetch,
  };
};

export const useBlueMoonActiveProperties = ({
  organizationId,
  enabled,
}: {
  organizationId: ?string,
  enabled?: boolean,
}) => {
  const isEnabled = Boolean(enabled && organizationId);
  const query = useQuery(
    ['blue-moon-active-properties', organizationId],
    () => getBlueMoonActiveProperties(organizationId),
    {
      enabled: isEnabled,
      refetchInterval: isEnabled ? LEASE_SIGNING_PROVIDER_REFRESH_MS : false,
      retry: false,
    },
  );
  // When the last 403 came. A list read before it stays dropped, even after a later network error,
  // until an authorized read succeeds.
  const forbiddenAt = useRef(0);
  if (query.error?.statusCode === 403) {
    forbiddenAt.current = Math.max(forbiddenAt.current, query.errorUpdatedAt);
  }

  // A disabled query still returns its cache, so a flag turned off at runtime would keep the badges.
  if (!isEnabled) {
    return {
      propertyIds: [],
      isLoading: false,
      isError: false,
      isForbidden: false,
    };
  }

  const { isLoading, isError, error } = query;
  // The route needs organization-wide lease-read. A refusal is no outage, so the last good list
  // gets no grace period and no badge outlives it.
  const isForbidden = error?.statusCode === 403;
  const data =
    isForbidden || query.dataUpdatedAt <= forbiddenAt.current
      ? undefined
      : lastGoodAnswer(query);
  return {
    propertyIds: data?.propertyIds ?? [],
    isLoading,
    isError: isError && data === undefined,
    isForbidden,
  };
};
